import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Modal, Button, Icon, Segment, Dropdown, Loader, Dimmer, Label
} from 'semantic-ui-react';
import { API, showError } from '../helpers';

const POLL_INTERVAL = 3000;

const linesOptions = [
  { key: 50, text: '50 行', value: 50 },
  { key: 100, text: '100 行', value: 100 },
  { key: 200, text: '200 行', value: 200 },
  { key: 500, text: '500 行', value: 500 },
];

const ModelLogModal = ({ open, onClose, modelId, modelName }) => {
  const [logs, setLogs] = useState('');
  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState(100);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const logRef = useRef(null);
  const timerRef = useRef(null);

  const loadLogs = useCallback(async (silent = false) => {
    if (!modelId) return;
    if (!silent) setLoading(true);
    try {
      const res = await API.get(`/api/model-manager/models/${modelId}/logs?lines=${lines}`);
      const { success, data, message } = res.data;
      if (success && data) {
        setLogs(prev => {
          const next = data.logs || '';
          if (prev !== next && logRef.current) {
            setTimeout(() => {
              if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
            }, 50);
          }
          return next;
        });
      } else {
        setLogs('Failed to load logs: ' + (message || 'unknown error'));
      }
    } catch (e) {
      if (!silent) { showError(e.message); setLogs('Error: ' + e.message); }
    }
    if (!silent) setLoading(false);
  }, [modelId, lines]);

  useEffect(() => {
    if (open && modelId) {
      loadLogs();
    } else {
      setLogs('');
    }
  }, [open, modelId, lines, loadLogs]);

  useEffect(() => {
    if (open && modelId && autoRefresh) {
      timerRef.current = setInterval(() => loadLogs(true), POLL_INTERVAL);
    }
    return () => clearInterval(timerRef.current);
  }, [open, modelId, autoRefresh, loadLogs]);

  return (
    <Modal open={open} onClose={onClose} size='large'>
      <Modal.Header>
        <Icon name='file alternate outline' /> 模型日志 - {modelName}
        <Dropdown
          selection
          compact
          options={linesOptions}
          value={lines}
          onChange={(e, { value }) => setLines(value)}
          style={{ marginLeft: 16, fontSize: '0.85em' }}
        />
        <Button
          icon='refresh'
          size='mini'
          style={{ marginLeft: 8 }}
          onClick={() => loadLogs()}
          loading={loading}
        />
        <Button
          size='mini'
          toggle
          active={autoRefresh}
          style={{ marginLeft: 4 }}
          onClick={() => setAutoRefresh(v => !v)}
        >
          <Icon name={autoRefresh ? 'pause' : 'play'} />
          {autoRefresh ? '实时' : '已暂停'}
          {autoRefresh && <Label circular color='green' size='mini' style={{ marginLeft: 4 }}>●</Label>}
        </Button>
      </Modal.Header>
      <Modal.Content scrolling>
        {loading ? (
          <Dimmer active inverted><Loader /></Dimmer>
        ) : (
          <Segment style={{ maxHeight: '500px', overflow: 'auto', padding: 0 }}>
            <div ref={logRef} style={{ height: '100%', overflow: 'auto' }}>
            <pre style={{
              fontSize: '12px',
              lineHeight: '1.4',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              margin: 0,
              padding: '12px',
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            }}>
              {logs || 'No logs available'}
            </pre>
            </div>
          </Segment>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>关闭</Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModelLogModal;
